import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import metricTooltips from '../config/metricTooltips';

/* Reusable ⓘ hover tooltip for dashboard metrics.
   - metricKey: key from metricTooltips config
   - color: Tailwind color name for theming (e.g. 'rose', 'green', 'blue')
   - inverted: true when icon sits on a dark/colored background (renders white icon)
   Uses a portal to render popover at document.body level, avoiding stacking context
   and overflow clipping from parent transform/backdrop-blur containers. */
const MetricTooltip = ({ metricKey, color = 'rose', inverted = false }) => {
  const [show, setShow] = useState(false);
  const [style, setStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [flipY, setFlipY] = useState(false);
  const iconRef = useRef(null);
  const tip = metricTooltips[metricKey];
  if (!tip) return null;

  const POPOVER_W = 288; // w-72 = 18rem = 288px
  const GAP = 12;
  const EDGE_PAD = 8;

  const handleEnter = () => {
    setShow(true);
    requestAnimationFrame(() => {
      const icon = iconRef.current;
      if (!icon) return;
      const ir = icon.getBoundingClientRect();
      const centerX = ir.left + ir.width / 2;

      /* Vertical: prefer above, flip below if not enough room */
      const flip = ir.top < 200;
      setFlipY(flip);

      /* Horizontal: center on icon, shift if overflowing viewport */
      let left = centerX - POPOVER_W / 2;
      if (left < EDGE_PAD) left = EDGE_PAD;
      else if (left + POPOVER_W > window.innerWidth - EDGE_PAD) left = window.innerWidth - EDGE_PAD - POPOVER_W;

      const top = flip ? ir.bottom + GAP : ir.top - GAP;

      setStyle({
        position: 'fixed',
        left: `${left}px`,
        top: flip ? `${top}px` : undefined,
        bottom: flip ? undefined : `${window.innerHeight - top}px`,
        width: `${POPOVER_W}px`,
        zIndex: 9999,
      });

      /* Arrow points at icon center */
      setArrowStyle({ left: `${centerX - left}px` });
    });
  };

  /* Icon classes: white icon on dark bg vs colored icon on light bg */
  const iconClass = inverted
    ? `cursor-help text-[11px] w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-white/30 text-white font-bold opacity-80 hover:opacity-100 hover:scale-125 hover:bg-white/50 group-hover:animate-pulse transition-all duration-300 ring-1 ring-white/40`
    : `cursor-help text-[11px] w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-${color}-50 text-${color}-500 font-bold opacity-70 hover:opacity-100 hover:scale-125 hover:shadow-lg hover:shadow-${color}-200/50 group-hover:animate-pulse transition-all duration-300 ring-1 ring-${color}-200/50`;

  return (
    <span className="relative inline-flex items-center ml-1.5 normal-case tracking-normal" ref={iconRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      <span className={iconClass}>ⓘ</span>
      {show && createPortal(
        <div style={style} className="animate-in fade-in zoom-in-95 duration-300 pointer-events-none">
          <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/60 overflow-hidden">
            {/* Gradient shimmer top bar */}
            <div className={`h-1 bg-gradient-to-r from-${color}-400 via-pink-400 to-${color}-500`} />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 rounded-lg bg-gradient-to-br from-${color}-400 to-${color}-600 flex items-center justify-center shadow-md`}>
                  <span className="text-white text-[10px] font-bold">✦</span>
                </div>
                {/* Solid color title - no gradient fade on long headings */}
                <p className={`text-sm font-serif font-bold text-${color}-700`}>{tip.title}</p>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed font-light">{tip.description}</p>
            </div>
            {/* Arrow - flips direction based on popover position */}
            <div className={`absolute ${flipY ? 'bottom-full -mb-px' : 'top-full -mt-px'}`} style={{ left: arrowStyle.left, transform: 'translateX(-50%)' }}>
              <div className={`w-3 h-3 bg-white/95 rotate-45 shadow-sm ${flipY ? 'border-l border-t border-white/60' : 'border-r border-b border-white/60'}`} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
};

export default MetricTooltip;
